import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/scanner`;

export const scannerApi = {
  config: () => axios.get(`${API}/config`).then((r) => r.data),
  resolve: (code, mode) => axios.get(`${API}/resolve`, { params: { code, mode } }).then((r) => r.data),
  resolveEmployee: (code) => axios.get(`${API}/resolve-employee`, { params: { code } }).then((r) => r.data),
  commit: (body) => axios.post(`${API}/commit`, body).then((r) => r.data),
  sessions: (params) => axios.get(`${API}/sessions`, { params }).then((r) => r.data),
  undo: (id) => axios.post(`${API}/sessions/${id}/undo`).then((r) => r.data),
  verifyReport: (params) => axios.get(`${API}/verify-report`, { params }).then((r) => r.data),
};

export const MODES = ['stock_in', 'stock_out', 'stocktake', 'lend', 'return', 'assign', 'verify', 'move', 'lookup', 'eng_use', 'eng_return', 'eng_scrap', 'bihin_stock'];
export const STOCK_MODES = ['stock_in', 'stock_out', 'stocktake'];
export const ASSET_MODES = ['lend', 'return', 'assign', 'verify', 'move', 'lookup', 'eng_use', 'eng_return', 'eng_scrap', 'bihin_stock'];
export const ENG_MODES = ['eng_use', 'eng_return', 'eng_scrap'];
// Modes that require picking a mfg dept before scanning
export const DEPT_REQUIRED_MODES = STOCK_MODES;
// Modes that need a borrower/assignee selection UI (single employee per session)
export const EMPLOYEE_REQUIRED_MODES = ['lend', 'assign'];

export const MODE_COLOR = {
  stock_in: 'from-emerald-500 to-teal-600',
  stock_out: 'from-rose-500 to-pink-600',
  stocktake: 'from-amber-500 to-orange-600',
  lend: 'from-violet-500 to-indigo-600',
  return: 'from-sky-500 to-blue-600',
  assign: 'from-blue-600 to-indigo-700',
  verify: 'from-teal-500 to-cyan-600',
  move: 'from-orange-500 to-red-500',
  lookup: 'from-slate-500 to-zinc-600',
};
export const MODE_BADGE = {
  stock_in: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  stock_out: 'bg-rose-50 text-rose-700 border-rose-200',
  stocktake: 'bg-amber-50 text-amber-700 border-amber-200',
  lend: 'bg-violet-50 text-violet-700 border-violet-200',
  return: 'bg-sky-50 text-sky-700 border-sky-200',
  assign: 'bg-blue-50 text-blue-700 border-blue-200',
  verify: 'bg-teal-50 text-teal-700 border-teal-200',
  move: 'bg-orange-50 text-orange-700 border-orange-200',
  lookup: 'bg-slate-50 text-slate-600 border-slate-200',
  eng_use: 'bg-teal-50 text-teal-700 border-teal-200',
  eng_return: 'bg-sky-50 text-sky-700 border-sky-200',
  eng_scrap: 'bg-rose-50 text-rose-700 border-rose-200',
  bihin_stock: 'bg-amber-50 text-amber-700 border-amber-200',
};

// Short beep + vibration feedback for scans. Each operation mode gets a
// DISTINCT pattern so operators can tell outcomes apart without looking at
// the screen (Feb 2026 handheld-style sound spec):
//   入庫       — rising 2-tone chirp,       haptic [30, 40, 60]
//   出庫       — falling 2-tone chirp,      haptic [60, 40, 30]
//   棚卸       — quick double blip,         haptic [30, 40, 30]
//   貸出       — bright ascending triad,    haptic [40, 40, 40]
//   返却       — descending settle,         haptic [80]
//   移動       — rolling chirp,             haptic [40, 40, 40, 40, 40]
//   照会       — single soft blip,          haptic [20]
//   検証 (棚卸検証) — like 棚卸
//   技術資産・備品 — reuse the closest kinematic (use↔lend, return↔return, scrap↔error)
// Error tone stays fixed (low square 220Hz + [60,40,60]) regardless of mode.
let _audioCtx = null;

// { freqs: number[]  — series of frequencies played sequentially
//   step:  number    — duration of each frequency in seconds
//   type:  OscillatorType }
const _MODE_TONE = {
  stock_in:   { freqs: [660, 990],           step: 0.06, type: 'sine' },
  stock_out:  { freqs: [990, 660],           step: 0.06, type: 'sine' },
  stocktake:  { freqs: [880, 0, 880],        step: 0.04, type: 'sine' },
  lend:       { freqs: [523, 659, 784],      step: 0.06, type: 'triangle' },
  return:     { freqs: [784, 659, 523],      step: 0.06, type: 'triangle' },
  assign:     { freqs: [523, 659, 784],      step: 0.06, type: 'triangle' },
  verify:     { freqs: [880, 0, 880],        step: 0.04, type: 'sine' },
  move:       { freqs: [660, 880, 660, 880], step: 0.05, type: 'sine' },
  lookup:     { freqs: [880],                step: 0.08, type: 'sine' },
  eng_use:    { freqs: [523, 659, 784],      step: 0.06, type: 'triangle' },
  eng_return: { freqs: [784, 659, 523],      step: 0.06, type: 'triangle' },
  eng_scrap:  { freqs: [660, 440, 220],      step: 0.08, type: 'triangle' },
  bihin_stock:{ freqs: [660, 990],           step: 0.06, type: 'sine' },
};
const _MODE_HAPTIC = {
  stock_in:   [30, 40, 60],
  stock_out:  [60, 40, 30],
  stocktake:  [30, 40, 30],
  lend:       [40, 40, 40],
  return:     [80],
  assign:     [40, 40, 40],
  verify:     [30, 40, 30],
  move:       [40, 40, 40, 40, 40],
  lookup:     [20],
  eng_use:    [40, 40, 40],
  eng_return: [80],
  eng_scrap:  [60, 40, 60],
  bihin_stock:[30, 40, 60],
};
const _DEFAULT_TONE = { freqs: [880], step: 0.12, type: 'sine' };
const _DEFAULT_HAPTIC = [40];

function _playTones(ctx, tone) {
  const { freqs, step, type } = tone;
  const t0 = ctx.currentTime;
  freqs.forEach((freq, i) => {
    if (!freq) return; // 0 means "silence" (gap between blips)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const start = t0 + i * step;
    const stop = start + step * 0.85;
    gain.gain.setValueAtTime(0.09, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, stop);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(start); osc.stop(stop);
  });
}

// scanFeedback(ok, mode?)
//   ok=false  → fixed error buzz + long triple vibration
//   ok=true   → mode-specific tone + haptic (falls back to a soft blip)
export function scanFeedback(ok = true, mode = null) {
  try {
    if (navigator.vibrate) {
      navigator.vibrate(ok ? (_MODE_HAPTIC[mode] || _DEFAULT_HAPTIC) : [60, 40, 60]);
    }
  } catch (err) { console.debug('[scanner] vibrate unsupported', err); }
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    if (ok) {
      _playTones(ctx, _MODE_TONE[mode] || _DEFAULT_TONE);
    } else {
      // Fixed error tone — square wave, low pitch, longer decay
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 220;
      gain.gain.setValueAtTime(0.09, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.26);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.27);
    }
  } catch (err) { console.debug('[scanner] audio unsupported', err); }
}

// scanCommitFeedback(mode) — distinctive "session complete" chime played
// when a batch of scans is successfully committed. Chord of three notes
// rooted on the mode's dominant tone so different operations feel distinct
// on commit too.
export function scanCommitFeedback(mode = null) {
  try {
    if (navigator.vibrate) navigator.vibrate([25, 30, 25, 30, 60]);
  } catch { /* ignore */ }
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const base = _MODE_TONE[mode]?.freqs?.[0] || 660;
    // Root + perfect fifth + octave, staggered for a light arpeggio.
    const notes = [base, Math.round(base * 1.5), base * 2];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const start = ctx.currentTime + i * 0.045;
      const stop = start + 0.28;
      gain.gain.setValueAtTime(0.06, start);
      gain.gain.exponentialRampToValueAtTime(0.0001, stop);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(start); osc.stop(stop);
    });
  } catch (err) { console.debug('[scanner] commit chime unsupported', err); }
}

